import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type SessionIngestionStatus,
  type SessionRecord,
} from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

/** How often a session with files not ingested yet is checked again. */
const PENDING_POLL_INTERVAL_MS = 15_000;

/** How often an in-progress session Roboto may complete on its own is checked again. */
const AWAITING_COMPLETION_POLL_INTERVAL_MS = 60_000;

/**
 * Where a session's ingestion stands. Refetched while it can change without
 * anyone acting on the page: every 15 seconds while any of its files is not
 * ingested yet, and every minute while the session is in progress with a
 * completion policy, since Roboto completes it on its own.
 *
 * When a response disagrees with the cached session record about whether the
 * session is complete, the record is refetched.
 */
export function useSessionIngestionStatus({
  session,
  enabled = true,
}: {
  session: Pick<SessionRecord, "session_id" | "status" | "completion_policy">;
  enabled?: boolean;
}): UseQueryResult<SessionIngestionStatus, Error> {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();
  const sessionId = session.session_id;
  const awaitsCompletion =
    session.status === "in_progress" && session.completion_policy !== null;
  return useQuery<SessionIngestionStatus, Error>({
    queryKey: sessionQueryKeys.ingestion(sessionId),
    queryFn: async ({ signal }) => {
      const status = await sessionService.getIngestionStatus(sessionId, {
        abortSignal: signal,
      });
      const record = queryClient.getQueryData<SessionRecord>(
        sessionQueryKeys.detail(sessionId),
      );
      if (
        record !== undefined &&
        (record.status === "in_progress") !== (status.state === "in_progress")
      ) {
        // Exact, so this status is not refetched along with the record.
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.detail(sessionId),
          exact: true,
        });
      }
      return status;
    },
    enabled,
    refetchInterval: (query) => {
      if ((query.state.data?.pending_file_count ?? 0) > 0) {
        return PENDING_POLL_INTERVAL_MS;
      }
      return awaitsCompletion ? AWAITING_COMPLETION_POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Whether the session is complete, by whichever was fetched last: its record
 * or its ingestion status. Roboto can complete a session on its own, and the
 * status poll sees that before the record is refetched.
 */
export function useIsSessionComplete(
  session: Pick<SessionRecord, "session_id" | "status">,
  status: UseQueryResult<SessionIngestionStatus, Error>,
): boolean {
  const queryClient = useQueryClient();
  const recordUpdatedAt =
    queryClient.getQueryState(sessionQueryKeys.detail(session.session_id))
      ?.dataUpdatedAt ?? 0;
  if (status.data === undefined || status.dataUpdatedAt < recordUpdatedAt) {
    return session.status === "complete";
  }
  return status.data.state !== "in_progress";
}
